import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { retryLicense } from '../../redux/slices/licenseSlice';

const REASON_LABELS = {
  NO_CONFIG: 'This install has not been licensed yet.',
  NOT_VALIDATED: 'The license has not been validated yet.',
  GRACE_EXPIRED: 'The license server has been unreachable past the grace period.',
  TAMPERED: 'Core application files appear to have been modified.',
  DEV_BYPASS: 'Running in development mode.',
  BAD_RESPONSE_SIGNATURE: 'The license server response could not be verified.',
  SUSPENDED: 'This license has been suspended.',
  EXPIRED: 'This license has expired.',
  IP_MISMATCH: 'This license is bound to a different network.',
  KEY_INVALID: 'This license key is invalid.',
  KEY_SHARE_ALERT: 'This license has been flagged for key sharing.',
  HTTP_403: 'The license server rejected this request.',
  HTTP_404: 'The license server could not be reached.',
  HTTP_400: 'The license server rejected this request.',
};

export default function LicenseScreen() {
  const dispatch = useDispatch();
  const { state, reason, message, loading, error, lastCheckedAt } = useSelector((s) => s.license);

  const locked = state === 'LOCKED' || state === 'UNPROVISIONED';
  const label = reason ? REASON_LABELS[reason] || message || reason : message;

  const handleRetry = () => {
    dispatch(retryLicense());
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-600 shadow-lg shadow-indigo-500/30 mb-4">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white">Devoard POS</h1>
          <p className="text-gray-400 text-sm mt-1">License Required</p>
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 shadow-2xl">
          <div className="flex items-start gap-3 mb-4">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide ${
                locked ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'
              }`}
            >
              {locked ? 'Locked' : 'Active'}
            </span>
            <span className="text-gray-500 text-xs font-mono uppercase tracking-wide mt-1">
              {reason || state}
            </span>
          </div>

          <p className="text-gray-300 text-sm leading-relaxed mb-6">
            {locked ? (label || 'This installation is not licensed.') : 'Your license is valid.'}
          </p>

          {error && (
            <p className="text-red-400 text-xs mb-4 bg-red-500/5 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="text-xs text-gray-500 space-y-1 mb-6">
            {lastCheckedAt && (
              <p>Last check: {new Date(lastCheckedAt).toLocaleString()}</p>
            )}
            <p>If you believe this is a mistake, contact Devoard support.</p>
          </div>

          <button
            onClick={handleRetry}
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm transition-colors"
          >
            {loading ? (
              <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            )}
            {loading ? 'Checking license…' : 'Retry'}
          </button>
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">Devoard License Server</p>
      </div>
    </div>
  );
}
